import { useCallback, useEffect, useMemo, useState } from "react";

import { ActionIcon, Anchor, Group, Table, Tooltip } from "@mantine/core"
import { notifications } from "@mantine/notifications";
import { format } from "@react-input/mask";
import { IconEdit, IconMailForward } from "@tabler/icons-react";
import { TFunction } from "i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "components";
import { IRootState } from "config/redux";
import { AppConstants, AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import { UserService } from "services";
import { AgentClientLimitsRequest, ProfileResponse, UserRequest, UserResponse, UserStatus } from "types";
import { AppUtils, UserUtils } from "utils";

import UserForm from "../form";


const { REQUEST_PENDING } = ServiceConstants;
const { ROLE_USR_AGT, ROLE_PRF_CUD } = AuthorityConstants;
const { MASK_SWISS_PHONE_NUMBER } = AppConstants;

interface UsersListItemProp {
  item: UserResponse;
  profiles: ProfileResponse[];
  t: TFunction;
  fetch: () => Promise<void>;
}

type UserEditFormRequest = UserRequest & Partial<AgentClientLimitsRequest>;

const UsersListItem = ({ item, fetch, profiles, t }: UsersListItemProp) => {
  const navigate = useNavigate();

  const getStatusColor = (status: UserStatus) => (
    status === 'ACTIVE' ? 'var(--mantine-color-primary-9)'
    : status === 'SUSPENDED' ? 'var(--mantine-color-error)'
    : ''
  );

  return (
    <>
      <Table.Tr key={item.id}>
        <Table.Td>
          {UserUtils.hasAuthority(ROLE_USR_AGT) ? (
            <Anchor c="green" onClick={() => navigate(`${RouteConstants.ROUTE_PROFILE}/${item.id}`, { state: { user: item } })}>
              {item.fullName}
            </Anchor>
          ) : (
            item.fullName
          )}
        </Table.Td>
        <Table.Td visibleFrom={'md'}>{item.countryCode && item.phoneNumber ? (item.countryCode + ' ' + item.phoneNumber) : ''}</Table.Td>
        <Table.Td>{item.email}</Table.Td>
        <Table.Td>
          {item.profile?.name ? (
            UserUtils.hasAuthority(ROLE_PRF_CUD) ? (
              <Anchor c="green" onClick={() => navigate(`${RouteConstants.ROUTE_PERMISSIONS}/agenti/${item.id}`)}>
                {item.profile.name}
              </Anchor>
            ) : (
              item.profile.name
            )
          ) : '-'}
        </Table.Td>
        <Table.Td c={getStatusColor(item.status)}>{t('user.status.' + item.status)}</Table.Td>
        <Table.Td ta={'right'}>
          <Group gap={4} justify="flex-end">
            <ResendVerificationCode item={item} t={t} />
            <UserEditModal item={item} fetch={fetch} profiles={profiles} t={t} />
          </Group>
        </Table.Td>
      </Table.Tr>
    </>
  )
}

const UserEditModal = ({ item, fetch, profiles, t }: UsersListItemProp) => {
  const [opened, setOpened] = useState<boolean>(false);
  const [isLimitsSaving, setIsLimitsSaving] = useState(false);
  const requestId = UserService.getUsersUpdateRequestId(item.id);
  const serviceObject = useSelector((state: IRootState) => state.service.object[requestId]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, requestId, async () => {
      await fetch();
      setOpened(false);
    })
  }, [serviceObject, requestId, fetch]);

  const onSubmit = useCallback(async (data: UserEditFormRequest) => {
    if (data.maxActiveClients !== undefined && data.maxReservedClients !== undefined) {
      setIsLimitsSaving(true);
      try {
        await UserService.updateAgentClientLimits(item.id, {
          maxActiveClients: data.maxActiveClients,
          maxReservedClients: data.maxReservedClients,
        });
      } catch {
        return;
      } finally {
        setIsLimitsSaving(false);
      }
    }

    UserService.usersUpdate(item.id, data);
  }, [item]);
  const isLoading = useMemo(
    () => isLimitsSaving || (serviceObject && serviceObject.status === REQUEST_PENDING),
    [isLimitsSaving, serviceObject],
  );

  if (!UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_U)) {
    return <></>;
  }

  return (
    <>
      <Button variant={'outline'} size={'compact-xs'} onClick={() => setOpened(true)}>
        <IconEdit size={14} />
      </Button>
      <UserForm
        item={{
          ...item,
          firstName: item.firstName ?? item.fullName.split(' ')[0],
          lastName: item.lastName ?? item.fullName.split(' ').slice(1).join(' '),
          phoneNumber: item.phoneNumber
            ? format(item.phoneNumber, {
              mask: MASK_SWISS_PHONE_NUMBER,
              replacement: { _: /\d/ },
            })
            : '',
          teamLeadId: item.teamLead?.id ?? null,
        } as UserRequest}
        loading={isLoading}
        opened={opened}
        onClose={() => setOpened(false)}
        onSubmit={onSubmit}
        profiles={profiles}
        t={t}
        title={t('user.modal.edit.any')}
      />
    </>
  )
}

const ResendVerificationCode = ({ item, t }: Pick<UsersListItemProp, "item" | "t">) => {
  const requestId = UserService.getRegistrationCodeResendRequestId(item.id);
  const serviceObject = useSelector((state: IRootState) => state.service.object[requestId]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, requestId, () => {
      notifications.show({
        color: "green",
        message: t("user.list.resendVerificationCodeSuccess", { email: item.email }),
      });
    });
  }, [serviceObject, requestId, item.email, t]);

  const isLoading = useMemo(
    () => serviceObject && serviceObject.status === REQUEST_PENDING,
    [serviceObject],
  );

  if (item.status !== "NOT_VERIFIED" || !UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_U)) {
    return <></>;
  }

  return (
    <Tooltip label={t("user.list.resendVerificationCode")} withArrow>
      <ActionIcon
        aria-label={t("user.list.resendVerificationCode")}
        color="primary"
        loading={isLoading}
        onClick={() => UserService.registrationCodeResend(item.id, item.email)}
        size="sm"
        variant="subtle"
      >
        <IconMailForward size={16} />
      </ActionIcon>
    </Tooltip>
  );
};

export default UsersListItem;
